/**
 * Mapbox GL JS version served from api.mapbox.com. Bump this when upgrading — the section
 * (sections/locations-layout.liquid) no longer names a version anywhere.
 *
 * The library is not vendored into /assets despite the no-CDN rule in CLAUDE.md: the map's
 * style, tiles, sprites and glyphs are fetched from Mapbox at runtime regardless, so a local
 * copy of the 1.9 MB bundle would remove neither the third-party request nor the IP
 * disclosure, while adding that bundle to every theme sync. See WHHA-867.
 */
const MAPBOX_GL_VERSION = '3.32.0';
const MAPBOX_GL_BASE_URL = `https://api.mapbox.com/mapbox-gl-js/v${MAPBOX_GL_VERSION}`;

/** @type {Promise<any> | null} Shared across instances so the library is only fetched once. */
let mapboxGlPromise = null;

/**
 * Load Mapbox GL JS and its stylesheet on demand.
 *
 * Deliberately not loaded from the section: at v1.2.0 the stylesheet was a render-blocking
 * <link> and the script ran during page load. Loading both here keeps them off the critical
 * path. It does not avoid contacting Mapbox: the map sits right under the breadcrumb, so the
 * observer fires on first layout and every Locations view still reaches Mapbox (WHHA-867).
 *
 * @returns {Promise<any>} Resolves with the `mapboxgl` global.
 */
function loadMapboxGl() {
  if (mapboxGlPromise) return mapboxGlPromise;

  // Both halves are awaited, not just the script: #initMap runs the moment this resolves, and
  // with mapbox-gl.css still in flight v3 warns and lays out controls, popups and markers
  // unstyled until it lands. A stylesheet failure rejects like a script one (WHHA-867 review).
  mapboxGlPromise = Promise.all([loadMapboxGlScript(), loadMapboxGlStylesheet()])
    .then(() => window.mapboxgl)
    .catch((error) => {
      // Clear the cache so a later instance (or a theme-editor re-render) can retry.
      mapboxGlPromise = null;
      throw error;
    });

  return mapboxGlPromise;
}

/**
 * Append the Mapbox GL script and resolve once it has run.
 */
function loadMapboxGlScript() {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${MAPBOX_GL_BASE_URL}/mapbox-gl.js`;
    script.async = true;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error(`Failed to load Mapbox GL JS v${MAPBOX_GL_VERSION}`)), {
      once: true,
    });
    document.head.appendChild(script);
  });
}

/**
 * Append the Mapbox GL stylesheet and resolve once it has applied.
 *
 * A link whose `sheet` is set has already applied, so there is nothing to wait for. Every
 * other case gets a fresh link: the browser serves the duplicate from cache and fires
 * `load` on the new element, which is less code than tracking one already in flight.
 */
function loadMapboxGlStylesheet() {
  const href = `${MAPBOX_GL_BASE_URL}/mapbox-gl.css`;
  if (document.querySelector(`link[href="${href}"]`)?.sheet) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const stylesheet = document.createElement('link');
    stylesheet.rel = 'stylesheet';
    stylesheet.href = href;
    stylesheet.addEventListener('load', () => resolve(), { once: true });
    stylesheet.addEventListener(
      'error',
      () => reject(new Error(`Failed to load Mapbox GL CSS v${MAPBOX_GL_VERSION}`)),
      {
        once: true,
      }
    );
    document.head.appendChild(stylesheet);
  });
}

/**
 * Store Locator Component
 * Renders a Mapbox map with location markers
 */
class StoreLocator extends HTMLElement {
  #map = null;
  #locationData = [];
  /** @type {IntersectionObserver | null} */
  #observer = null;

  static get observedAttributes() {
    return ['data-access-token', 'data-map-zoom', 'data-map-center-latitude', 'data-map-center-longitude'];
  }

  get accessToken() {
    return this.dataset.accessToken || '';
  }

  get mapZoom() {
    return parseFloat(this.dataset.mapZoom) || 15;
  }

  get mapCenterLatitude() {
    return parseFloat(this.dataset.mapCenterLatitude) || 0;
  }

  get mapCenterLongitude() {
    return parseFloat(this.dataset.mapCenterLongitude) || 0;
  }

  get mapContainer() {
    return this.querySelector('[data-map-container]');
  }

  get locationBlocks() {
    return this.querySelectorAll('[data-location-block]');
  }

  connectedCallback() {
    // No blocks configured means no map container was rendered; nothing to load.
    if (!this.mapContainer) return;

    // Wait until the map is in or near the viewport before fetching Mapbox GL JS.
    if (!('IntersectionObserver' in window)) {
      this.#loadAndInitMap();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#disconnectObserver();
        this.#loadAndInitMap();
      },
      // Far enough out that the download has usually started by the time the map is visible.
      { rootMargin: '400px' }
    );

    this.#observer.observe(this.mapContainer);
  }

  disconnectedCallback() {
    this.#disconnectObserver();

    if (this.#map) {
      this.#map.remove();
      this.#map = null;
    }
  }

  #disconnectObserver() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /**
   * Fetch the library, then build the map — unless the element was removed while the
   * request was in flight, which happens routinely on theme editor re-renders.
   */
  async #loadAndInitMap() {
    try {
      await loadMapboxGl();
    } catch (error) {
      console.error(error);
      return;
    }

    // isConnected is false by the time a load resolves for an element the editor removed.
    if (!this.isConnected || this.#map) return;

    this.#initMap();
  }

  /**
   * Initialize the Mapbox map
   */
  #initMap() {
    if (!this.accessToken) {
      console.error('Mapbox access token is missing');
      return;
    }

    if (!this.mapContainer) {
      console.error('Map container not found');
      return;
    }

    mapboxgl.accessToken = this.accessToken;

    this.#map = new mapboxgl.Map({
      container: this.mapContainer,
      style: 'mapbox://styles/mosowld/ck3oj8q6z7kjj1cpjlspkfbsz',
      center: [this.mapCenterLongitude, this.mapCenterLatitude],
      zoom: this.mapZoom,
    });

    // Add navigation controls
    this.#map.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Generate location data and add markers
    this.#generateLocationData();
    this.#addMarkers();
  }

  /**
   * Generate location data from DOM elements
   */
  #generateLocationData() {
    this.#locationData = [];

    this.locationBlocks.forEach((block, index) => {
      const latitude = parseFloat(block.dataset.locationLatitude);
      const longitude = parseFloat(block.dataset.locationLongitude);

      if (isNaN(latitude) || isNaN(longitude)) return;

      this.#locationData.push({
        index,
        coordinates: [longitude, latitude],
        name: block.dataset.locationName || '',
        address: block.dataset.locationAddress || '',
        phone: block.dataset.locationPhone || '',
        hours: block.dataset.locationHours || '',
        details: block.dataset.locationDetails || '',
        iconUrl: block.dataset.locationIcon || '',
      });
    });
  }

  /**
   * Add markers to the map
   */
  #addMarkers() {
    this.#locationData.forEach((location) => {
      const markerOptions = { anchor: 'bottom' };

      // Create custom marker element if icon is provided
      if (location.iconUrl) {
        const el = document.createElement('div');
        el.className = `location__marker location__marker--${location.index}`;
        el.style.cssText = `
          background-image: url(${location.iconUrl});
          width: 30px;
          height: 30px;
          background-size: contain;
          background-repeat: no-repeat;
          cursor: pointer;
        `;
        markerOptions.element = el;
      }

      // Create popup
      const popup = new mapboxgl.Popup({
        offset: 25,
        closeButton: false,
      }).setHTML(this.#formatPopupContent(location));

      // Pan map to center popup on mobile
      popup.on('open', () => {
        if (window.matchMedia('(max-width: 768px)').matches) {
          this.#map.panTo(location.coordinates, {
            duration: 300,
            offset: [0, -100],
          });
        }
      });

      // Create and add marker
      new mapboxgl.Marker(markerOptions).setLngLat(location.coordinates).setPopup(popup).addTo(this.#map);
    });
  }

  /**
   * Format location data into HTML for popup
   * @param {Object} location - Location data object
   * @returns {string} HTML string for popup
   */
  #formatPopupContent(location) {
    // dataset decodes the Liquid escaping, so re-escape before setHTML. Address and hours keep their line breaks. WHHA-869
    const esc = (value) =>
      value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    const br = (value) => esc(value).replace(/\n/g, '<br>');
    let html = '<div class="location__description">';

    if (location.name) {
      html += `<h2 class="location__name">${esc(location.name)}</h2>`;
    }
    if (location.address) {
      html += `<p class="location__address">${br(location.address)}</p>`;
    }
    if (location.phone) {
      html += `<a href="tel:${esc(location.phone)}" class="location__phone">${esc(location.phone)}</a>`;
    }
    if (location.hours) {
      html += `<p class="location__hours">${br(location.hours)}</p>`;
    }
    if (location.details) {
      html += `<p class="location__details">${esc(location.details)}</p>`;
    }

    html += '</div>';
    return html;
  }

  /**
   * Refresh the map (useful for theme editor updates)
   */
  refresh() {
    if (this.#map) {
      this.#map.remove();
      this.#map = null;
    }
    // Goes back through the loader rather than straight to #initMap: a refresh can fire
    // before the library has been fetched, since the fetch now waits on the viewport.
    this.#loadAndInitMap();
  }
}

if (!customElements.get('store-locator')) {
  customElements.define('store-locator', StoreLocator);
}
